import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  InvoiceMailAttachmentPreview,
  isPdfAttachment,
} from "@/components/invoiceMail/InvoiceMailAttachmentPreview";
import { ApiError } from "@/lib/apiClient";
import { formatEuro } from "@/lib/formatMoney";
import { fmtDateTime } from "@/pages/anfragen/anfrageWizardUtils";
import {
  approverKuerzel,
  approvalStatusClass,
  approvalStatusLabel,
  assignInvoiceApproval,
  type InvoiceApprovalApprover,
} from "@/lib/invoiceApproval";
import { toast } from "@/lib/toast";
import {
  confirmInvoiceMailCandidate,
  confidenceBadgeClass,
  confidenceLabel,
  dismissInvoiceMailCandidate,
  einvoiceLineCountFromCandidate,
  getInvoiceMailEinvoice,
  invoiceMailAttachmentUrl,
  MERGED_INVOICE_ATTACHMENT_ID,
  mergeInvoiceMailAttachments,
  reanalyzeInvoiceMailCandidate,
  searchInvoiceMailBestellnummern,
  stammMatchLabel,
  unmergeInvoiceMailAttachments,
  updateInvoiceMailMatches,
  type EingangsrechnungArtikelLine,
  type InvoiceMailAttachmentSummary,
  type InvoiceMailBestellnummerSearchItem,
  type InvoiceMailCandidate,
  type InvoiceMailEinvoiceResponse,
} from "@/lib/invoiceMail";

type SelectedTarget = {
  auftrag_id: number;
  bestellnummer: string;
  label: string;
  matchKind: "bestellung" | "projekt";
  displayNumber: string;
};

function targetKey(row: Pick<SelectedTarget, "auftrag_id" | "matchKind" | "displayNumber">): string {
  return `${row.auftrag_id}|${row.matchKind}|${row.displayNumber}`;
}

function isProjectMatch(t: {
  match_kind?: string | null;
  bestellnummer?: string | null;
  auftragsnummer?: string | null;
}): boolean {
  if (t.match_kind === "projekt") return true;
  if (t.match_kind === "bestellung") return false;
  return !(t.bestellnummer || "").trim() && !!(t.auftragsnummer || "").trim();
}

function projectDetails(t: {
  jahr?: number | null;
  kunde?: string | null;
  auftrag_beschreibung?: string | null;
}): string {
  return [t.jahr ? String(t.jahr) : "", (t.kunde || "").trim(), (t.auftrag_beschreibung || "").trim()]
    .filter(Boolean)
    .join(" – ");
}

function projectLabel(t: {
  auftragsnummer?: string | null;
  auftrag_id?: number | null;
  jahr?: number | null;
  kunde?: string | null;
  auftrag_beschreibung?: string | null;
}): string {
  const details = projectDetails(t);
  return `Projekt ${t.auftragsnummer || t.auftrag_id}${details ? ` – ${details}` : ""}`;
}

function fmtAttachmentSize(bytes?: number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${Math.round(n)} B`;
  return `${Math.round(n / 1024)} KB`;
}

function fmtQty(value?: number | null): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function senderLabel(row: InvoiceMailCandidate): string {
  const name = (row.from_name || "").trim();
  const email = (row.from_email || "").trim();
  if (name && email) return `${name} <${email}>`;
  return name || email || "Unbekannter Absender";
}

function toSelectedTarget(
  t: InvoiceMailBestellnummerSearchItem | {
    auftrag_id?: number | null;
    bestellnummer?: string | null;
    auftragsnummer?: string | null;
    sharepoint_folder_id?: string | null;
    match_kind?: "bestellung" | "projekt" | null;
    jahr?: number | null;
    kunde?: string | null;
    auftrag_beschreibung?: string | null;
  },
): SelectedTarget | null {
  if (t.auftrag_id == null || !t.sharepoint_folder_id) return null;
  const bestellnummer = (t.bestellnummer || "").trim();
  const project = isProjectMatch(t);
  if (!bestellnummer && !project) return null;
  if (project && !(t.auftragsnummer || "").trim()) return null;
  return {
    auftrag_id: t.auftrag_id,
    bestellnummer,
    matchKind: project ? "projekt" : "bestellung",
    displayNumber: project ? (t.auftragsnummer || "").trim() : bestellnummer,
    label: project
      ? projectLabel(t)
      : `Auftrag ${t.auftragsnummer || t.auftrag_id} – ${bestellnummer}`,
  };
}

function initialSelectedTargets(candidate: InvoiceMailCandidate): SelectedTarget[] {
  const targets = candidate.match_targets || [];
  const manual = (candidate.match_reasons || []).includes("manual");
  const orders = targets.filter((target) => !isProjectMatch(target));
  const projects = targets.filter((target) => isProjectMatch(target));
  const chosen = [...orders];
  if (manual || (orders.length === 0 && projects.length === 1)) {
    chosen.push(...projects);
  }
  const out: SelectedTarget[] = [];
  const seen = new Set<string>();
  for (const target of chosen) {
    const row = toSelectedTarget(target);
    if (!row) continue;
    const key = targetKey(row);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

function statusBadgeClass(status: InvoiceMailCandidate["status"]): string {
  switch (status) {
    case "pending":
      return "bg-status-orange/15 text-status-orange";
    case "confirmed":
      return "bg-status-green/15 text-status-green";
    case "dismissed":
      return "bg-label-secondary/15 text-label-secondary";
    default:
      return "bg-label-secondary/10 text-label-secondary";
  }
}

function statusLabel(status: InvoiceMailCandidate["status"]): string {
  switch (status) {
    case "pending":
      return "Offen";
    case "confirmed":
      return "Abgelegt";
    case "dismissed":
      return "Verworfen";
    case "skipped":
      return "Ohne Anhang";
    default:
      return status;
  }
}

function initAttachmentTks(candidate: InvoiceMailCandidate): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const att of candidate.attachments_summary || []) {
    const id = att.id ? String(att.id) : "";
    if (!id) continue;
    out[id] = [...(att.matched_tks || [])];
  }
  return out;
}

type WizardStep = 1 | 2 | 3;

const WIZARD_STEPS: Array<{ step: WizardStep; label: string; hint: string }> = [
  { step: 1, label: "Prüfen", hint: "Beleg und erkannte Nummern" },
  { step: 2, label: "Ablegen", hint: "Projektordner in SharePoint" },
  { step: 3, label: "Freigabe", hint: "Mitarbeiter zuweisen" },
];

function detectedKuerzels(row: InvoiceMailCandidate): string[] {
  const suggestion = row.assignment_suggestion;
  const listed = (suggestion?.kuerzels || []).map((code) => String(code || "").trim().toUpperCase()).filter(Boolean);
  if (listed.length > 0) return [...new Set(listed)];
  const single = String(suggestion?.kuerzel || "").trim().toUpperCase();
  return single ? [single] : [];
}

function keepMailContext(current: InvoiceMailCandidate, next: InvoiceMailCandidate): InvoiceMailCandidate {
  return {
    ...current,
    ...next,
    assignment_suggestion: next.assignment_suggestion ?? current.assignment_suggestion,
    approval: next.approval ?? current.approval,
  };
}

function isMergeableMailFile(att: InvoiceMailAttachmentSummary): boolean {
  if (att.merged) return true;
  const name = (att.name || "").toLowerCase();
  const type = (att.contentType || "").toLowerCase();
  if (type.includes("svg") || name.endsWith(".svg")) return false;
  if (type.includes("pdf") || name.endsWith(".pdf")) return true;
  if (type.startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|tif|tiff|bmp)$/.test(name);
}

function fileBelongsToInvoice(att: InvoiceMailAttachmentSummary): boolean {
  if (att.merged_source) return false;
  const name = (att.name || "").toLowerCase();
  const type = (att.contentType || "").toLowerCase();
  const inline = Boolean(att.is_inline || att.isInline);
  if (type.includes("pdf") || name.endsWith(".pdf")) return true;
  if (inline) return false;
  if (/logo|signatur|signature|banner|spacer|pixel/.test(name)) return false;
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(name)) {
    return Number(att.size || 0) >= 30_000;
  }
  return true;
}

function fileExclusionHint(att: InvoiceMailAttachmentSummary): string | null {
  if (att.merged_source) return "In der zusammengefügten Datei";
  if (fileBelongsToInvoice(att)) return null;
  const name = (att.name || "").toLowerCase();
  if (/logo/.test(name)) return "Logo";
  if (att.is_inline || att.isInline) return "Eingebettetes Bild";
  if ((att.contentType || "").toLowerCase().startsWith("image/")) return "Kleines Bild";
  return "Kein Beleg";
}

function initialAssignees(candidate: InvoiceMailCandidate, approvers: InvoiceApprovalApprover[]): string[] {
  const existing = (candidate.approval?.participants || []).map((person) => person.login).filter(Boolean);
  if (existing.length > 0) return existing;
  if (candidate.approval?.assignee_login) return [candidate.approval.assignee_login];
  const codes = detectedKuerzels(candidate);
  const matched = approvers
    .filter((person) => codes.includes(approverKuerzel(person)))
    .map((person) => person.login);
  if (matched.length > 0) return matched;
  const suggested = candidate.assignment_suggestion?.login;
  return suggested ? [suggested] : [];
}

function startStep(candidate: InvoiceMailCandidate): WizardStep {
  if (candidate.status === "confirmed") return 3;
  return 1;
}

function approvalLocked(status: string | undefined): boolean {
  return status === "freigegeben" || status === "abgelehnt" || status === "warte_zweite_freigabe";
}

type SummaryPerson = {
  key: string;
  name: string;
  kuerzel: string;
  decision?: string | null;
};

function summaryPeople(
  candidate: InvoiceMailCandidate,
  approvers: InvoiceApprovalApprover[],
): SummaryPerson[] {
  const approval = candidate.approval;
  const participants = approval?.participants || [];
  const rows =
    participants.length > 0
      ? participants
      : approval?.assignee_login
        ? [{ login: approval.assignee_login, name: approval.assignee_name, decision: null }]
        : [];
  return rows
    .filter((person) => person.login)
    .map((person) => {
      const approver = approvers.find((item) => item.login.toLowerCase() === person.login.toLowerCase());
      return {
        key: person.login,
        name: (person.name || approver?.name || person.login).trim(),
        kuerzel: approver ? approverKuerzel(approver) : "",
        decision: person.decision,
      };
    });
}

function approvalProgressText(status: string, count: number): string {
  switch (status) {
    case "freigegeben":
      return "Sachlich freigegeben.";
    case "abgelehnt":
      return "Abgelehnt.";
    case "in_klaerung":
      return "Liegt in Klärung.";
    case "warte_zweite_freigabe":
      return "Wartet auf die zweite Freigabe.";
    default:
      return count > 1
        ? "Wartet darauf, dass alle zugewiesenen Personen freigeben."
        : "Wartet auf die sachliche Freigabe der zugewiesenen Person.";
  }
}

function personDecisionLabel(decision: string | null | undefined, group: boolean): string | null {
  if (decision === "freigegeben") return "Hat freigegeben";
  if (decision === "abgelehnt") return "Hat abgelehnt";
  return group ? "Freigabe noch offen" : null;
}

function AssignmentDonePanel({
  candidate,
  targets,
  links,
  codes,
  approvers,
  einvoiceLines,
  locked,
  onEdit,
}: {
  candidate: InvoiceMailCandidate;
  targets: SelectedTarget[];
  links: Array<{ key: string; href?: string; label: string }>;
  codes: string[];
  approvers: InvoiceApprovalApprover[];
  einvoiceLines: number;
  locked: boolean;
  onEdit: () => void;
}) {
  const people = summaryPeople(candidate, approvers);
  const numbers = candidate.matched_bestellnummern || [];
  const status = candidate.approval?.status || "offen";
  const filedBy = (candidate.confirmed_by || "").trim();
  const filedByName =
    approvers.find((item) => item.login.toLowerCase() === filedBy.toLowerCase())?.name || filedBy;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-status-green/30 bg-status-green/10 px-3 py-3">
        <p className="font-semibold text-label">Hier ist nichts mehr zu tun.</p>
        <p className="mt-1 text-label-secondary">
          Die Rechnung ist geprüft, abgelegt und zugewiesen. Die sachliche Freigabe läuft in der Rechnungsfreigabe.
        </p>
      </div>

      <div className="divide-y divide-separator overflow-hidden rounded-xl border border-separator bg-card">
        <div className="px-3 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Zusammenfassung</div>
        </div>
        <div className="px-3 py-3">
          <div className="text-xs font-semibold text-label-tertiary">Prüfen</div>
          {numbers.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {numbers.map((tk) => (
                <span key={tk} className="rounded-md bg-accent/10 px-2 py-0.5 font-mono text-xs text-accent">
                  {tk}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-1 text-label-secondary">Keine Bestell- oder Projektnummer erkannt.</p>
          )}
          {einvoiceLines > 0 ? (
            <p className="mt-2 text-xs text-label-secondary">
              E-Rechnung mit {einvoiceLines} Position{einvoiceLines === 1 ? "" : "en"}.
            </p>
          ) : null}
        </div>
        <div className="px-3 py-3">
          <div className="text-xs font-semibold text-label-tertiary">Ablegen</div>
          {targets.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {targets.map((target) => (
                <li key={targetKey(target)}>
                  <span className="font-mono text-xs text-accent">{target.displayNumber}</span>
                  <div className="text-label">{target.label}</div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-label-secondary">Im Projektordner abgelegt.</p>
          )}
          {links.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs">
              {links.map((file) => (
                <li key={file.key} className="truncate">
                  {file.href ? (
                    <a href={file.href} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                      {file.label}
                    </a>
                  ) : (
                    <span className="text-label-secondary">{file.label}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {candidate.confirmed_at ? (
            <p className="mt-2 text-xs text-label-tertiary">
              {filedByName ? `Abgelegt von ${filedByName} · ` : "Abgelegt · "}
              {fmtDateTime(candidate.confirmed_at)}
            </p>
          ) : null}
        </div>
        <div className="px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs font-semibold text-label-tertiary">Freigabe</div>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${approvalStatusClass(status)}`}>
              {approvalStatusLabel(status)}
            </span>
          </div>
          <p className="mt-1 text-label-secondary">{approvalProgressText(status, people.length)}</p>
          {people.length > 0 ? (
            <ul className="mt-2 space-y-1.5">
              {people.map((person) => {
                const decision = personDecisionLabel(person.decision, people.length > 1);
                return (
                  <li
                    key={person.key}
                    className="flex items-center justify-between gap-3 rounded-lg border border-separator px-3 py-2"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-label">{person.name}</span>
                      {decision ? (
                        <span
                          className={clsx(
                            "text-xs",
                            person.decision === "freigegeben"
                              ? "text-status-green"
                              : person.decision === "abgelehnt"
                                ? "text-status-red"
                                : "text-label-secondary",
                          )}
                        >
                          {decision}
                        </span>
                      ) : null}
                    </span>
                    {person.kuerzel ? (
                      <span className="shrink-0 rounded-md bg-fill-tertiary px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-label">
                        {person.kuerzel}
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          {candidate.approval?.second_assignee_name &&
          !people.some((person) => person.name === candidate.approval?.second_assignee_name) ? (
            <p className="mt-2 text-xs text-label-secondary">
              Zweite Freigabe: {candidate.approval.second_assignee_name}
            </p>
          ) : null}
          {codes.length > 0 ? (
            <div className="mt-3">
              <div className="text-xs text-label-tertiary">Erkannte Kürzel auf der Rechnung</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {codes.map((code) => (
                  <span key={code} className="rounded-md bg-fill-tertiary px-2 py-0.5 font-mono text-xs font-semibold text-label">
                    {code}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Link to="/bestellwesen/rechnungsfreigabe" className="text-sm text-accent hover:underline">
          Zur Rechnungsfreigabe
        </Link>
        {locked ? null : (
          <Button type="button" variant="secondary" size="sm" onClick={onEdit}>
            Zuweisung ändern
          </Button>
        )}
      </div>
    </div>
  );
}

export function ConfirmInvoiceMailDialog({
  open,
  candidate,
  defaultSubfolder,
  approvers,
  hasNext = false,
  onClose,
  onConfirmed,
  onUpdated,
  onAssigned,
  onDismissed,
  onReanalyzed,
  onNext,
}: {
  open: boolean;
  candidate: InvoiceMailCandidate | null;
  defaultSubfolder: string;
  approvers: InvoiceApprovalApprover[];
  hasNext?: boolean;
  onClose: () => void;
  onConfirmed: () => void;
  onUpdated?: (candidate: InvoiceMailCandidate) => void;
  onAssigned?: (candidateId: number, assigneeLogin: string, assigneeName: string, status: string) => void;
  onDismissed?: () => void;
  onReanalyzed?: () => Promise<InvoiceMailCandidate | null>;
  onNext?: () => void;
}) {
  const [localCandidate, setLocalCandidate] = useState<InvoiceMailCandidate | null>(null);
  const [selectedTargets, setSelectedTargets] = useState<SelectedTarget[]>([]);
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [fileOrder, setFileOrder] = useState<string[]>([]);
  const [mergeAction, setMergeAction] = useState<"merge" | "split" | null>(null);
  const merging = mergeAction != null;
  const [attachmentTks, setAttachmentTks] = useState<Record<string, string[]>>({});
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [newTkInput, setNewTkInput] = useState("");
  const [includeEml, setIncludeEml] = useState(true);
  const [moveInbox, setMoveInbox] = useState(true);
  const [loading, setLoading] = useState(false);
  const [savingMatches, setSavingMatches] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState<string | null>(null);
  const [step, setStep] = useState<WizardStep>(1);
  const [assigneeLogins, setAssigneeLogins] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [discardAsk, setDiscardAsk] = useState(false);
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);

  const [searchQ, setSearchQ] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<InvoiceMailBestellnummerSearchItem[]>([]);

  const [einvoiceLoading, setEinvoiceLoading] = useState(false);
  const [einvoice, setEinvoice] = useState<InvoiceMailEinvoiceResponse | null>(null);

  const activeCandidate =
    open && candidate
      ? localCandidate && localCandidate.id === candidate.id
        ? localCandidate
        : candidate
      : null;

  const attachments = useMemo(
    () => (activeCandidate?.attachments_summary || []).filter((a) => a.id),
    [activeCandidate],
  );
  const orderedAttachments = useMemo(() => {
    const byId = new Map(attachments.map((item) => [String(item.id), item]));
    const seen = new Set<string>();
    const ordered: InvoiceMailAttachmentSummary[] = [];
    for (const id of fileOrder) {
      const item = byId.get(id);
      if (!item || seen.has(id)) continue;
      seen.add(id);
      ordered.push(item);
    }
    for (const item of attachments) {
      const id = String(item.id);
      if (seen.has(id)) continue;
      seen.add(id);
      ordered.push(item);
    }
    return ordered;
  }, [attachments, fileOrder]);
  const selectedMergeIds = orderedAttachments
    .filter((item) => selectedAttachments.has(String(item.id)) && isMergeableMailFile(item))
    .map((item) => String(item.id));
  const blockingMerge = orderedAttachments.find(
    (item) => selectedAttachments.has(String(item.id)) && !isMergeableMailFile(item),
  );
  const hasMergedFile = orderedAttachments.some((item) => item.merged);

  const resolvedPreviewId = (() => {
    if (previewId && attachments.some((attachment) => String(attachment.id) === previewId)) {
      return previewId;
    }
    const fallback = attachments.find((attachment) => isPdfAttachment(attachment)) || attachments[0];
    return fallback?.id ? String(fallback.id) : null;
  })();

  const previewAtt = attachments.find((a) => String(a.id) === resolvedPreviewId) || null;
  const einvoiceLines = activeCandidate ? einvoiceLineCountFromCandidate(activeCandidate) : 0;
  const isPending = activeCandidate?.status === "pending";

  useEffect(() => {
    if (!open || !candidate) return;
    setLocalCandidate(candidate);
    setIncludeEml(true);
    setMoveInbox(true);
    setError(null);
    setSaveOk(null);
    setSearchQ("");
    setSearchResults([]);
    setNewTkInput("");
    setStep(startStep(candidate));
    setAssigneeLogins(initialAssignees(candidate, approvers));
    setDiscardAsk(false);
    setShowAssigneePicker(false);

    setSelectedTargets(initialSelectedTargets(candidate));
    setAttachmentTks(initAttachmentTks(candidate));

    const summaryIds = (candidate.attachments_summary || [])
      .filter((a) => a?.id != null && fileBelongsToInvoice(a))
      .map((a) => String(a.id));
    setSelectedAttachments(new Set(summaryIds));
    setFileOrder(
      (candidate.attachments_summary || [])
        .filter((item) => item?.id != null)
        .map((item) => String(item.id)),
    );

    const defaultPreview =
      (candidate.attachments_summary || []).find((a) => isPdfAttachment(a))?.id ||
      candidate.attachments_summary?.[0]?.id ||
      null;
    setPreviewId(defaultPreview ? String(defaultPreview) : null);

    setEinvoice(null);
    setEinvoiceLoading(true);
    let cancelled = false;
    void getInvoiceMailEinvoice(candidate.id)
      .then((data) => {
        if (!cancelled) setEinvoice(data);
      })
      .catch(() => {
        if (!cancelled) setEinvoice(null);
      })
      .finally(() => {
        if (!cancelled) setEinvoiceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, candidate, approvers]);

  useEffect(() => {
    if (!open) return;
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setSearchLoading(true);
      void searchInvoiceMailBestellnummern(q)
        .then(setSearchResults)
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [open, searchQ]);

  function removeTarget(row: SelectedTarget) {
    const key = targetKey(row);
    setSelectedTargets((prev) => prev.filter((item) => targetKey(item) !== key));
  }

  function addSearchResult(item: InvoiceMailBestellnummerSearchItem) {
    const row = toSelectedTarget(item);
    if (!row) return;
    const key = targetKey(row);
    setSelectedTargets((prev) => {
      if (prev.some((item) => targetKey(item) === key)) return prev;
      return [...prev, row];
    });
    setSearchQ("");
    setSearchResults([]);
  }

  function toggleAttachment(id: string) {
    setSelectedAttachments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function moveFile(id: string, direction: -1 | 1) {
    const ids = orderedAttachments.map((item) => String(item.id));
    const index = ids.indexOf(id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    const moved = next[index];
    if (!moved) return;
    next.splice(index, 1);
    next.splice(target, 0, moved);
    setFileOrder(next);
  }

  async function handleMerge() {
    if (!activeCandidate) return;
    if (blockingMerge) {
      setError(`„${blockingMerge.name || "Datei"}“ lässt sich nicht zusammenfügen. Es gehen nur PDF-Dateien und Bilder.`);
      return;
    }
    if (selectedMergeIds.length < 2) {
      setError("Bitte mindestens zwei PDF-Dateien oder Bilder anhaken.");
      return;
    }
    setMergeAction("merge");
    setError(null);
    try {
      const res = await mergeInvoiceMailAttachments(activeCandidate.id, selectedMergeIds);
      const next = keepMailContext(activeCandidate, res.candidate);
      setLocalCandidate(next);
      onUpdated?.(next);
      setPreviewId(MERGED_INVOICE_ATTACHMENT_ID);
      toast.success("Die Belege sind zu einer Datei zusammengefügt.");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Zusammenfügen fehlgeschlagen.";
      setError(msg);
    } finally {
      setMergeAction(null);
    }
  }

  async function handleUnmerge() {
    if (!activeCandidate) return;
    setMergeAction("split");
    setError(null);
    try {
      const res = await unmergeInvoiceMailAttachments(activeCandidate.id);
      const next = keepMailContext(activeCandidate, res.candidate);
      setLocalCandidate(next);
      onUpdated?.(next);
      toast.success("Die Zusammenführung ist aufgehoben.");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Auflösen fehlgeschlagen.";
      setError(msg);
    } finally {
      setMergeAction(null);
    }
  }

  function addTkToPreviewAttachment(tk: string) {
    const attachmentId = resolvedPreviewId;
    if (!attachmentId) return;
    const cleaned = tk.trim();
    if (!cleaned) return;
    setAttachmentTks((prev) => {
      const current = prev[attachmentId] || [];
      if (current.includes(cleaned)) return prev;
      return { ...prev, [attachmentId]: [...current, cleaned] };
    });
    setNewTkInput("");
  }

  function removeTkFromPreviewAttachment(tk: string) {
    const attachmentId = resolvedPreviewId;
    if (!attachmentId) return;
    setAttachmentTks((prev) => ({
      ...prev,
      [attachmentId]: (prev[attachmentId] || []).filter((x) => x !== tk),
    }));
  }

  async function handleSaveMatches() {
    if (!activeCandidate) return;
    if (selectedTargets.length === 0) {
      setError("Bitte mindestens eine Bestellnummer / ein Ziel hinterlegen.");
      return;
    }
    setSavingMatches(true);
    setError(null);
    setSaveOk(null);
    try {
      const res = await updateInvoiceMailMatches(activeCandidate.id, {
        targets: selectedTargets.map((t) => ({
          auftrag_id: t.auftrag_id,
          bestellnummer: t.bestellnummer || null,
          subfolder: defaultSubfolder,
        })),
        attachment_tks: attachmentTks,
      });
      setLocalCandidate(res.candidate);
      onUpdated?.(res.candidate);
      setSaveOk("Zuordnung gespeichert.");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Speichern fehlgeschlagen.";
      setError(msg);
    } finally {
      setSavingMatches(false);
    }
  }

  async function handleConfirm() {
    if (!activeCandidate) return;
    if (selectedTargets.length === 0) {
      setError("Bitte mindestens einen Zielordner auswählen oder korrigieren.");
      return;
    }
    if (selectedAttachments.size === 0) {
      setError("Bitte mindestens eine Datei für die Ablage auswählen. Logos sind voraus abgewählt.");
      setStep(1);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await confirmInvoiceMailCandidate(activeCandidate.id, {
        sharepoint_targets: selectedTargets.map((t) => ({
          auftrag_id: t.auftrag_id,
          bestellnummer: t.bestellnummer || null,
          subfolder: defaultSubfolder,
        })),
        include_eml: includeEml,
        selected_attachment_ids: selectedAttachments.size > 0 ? [...selectedAttachments] : [],
        move_in_mailbox_to: moveInbox ? "Verarbeitet" : null,
      });
      const filed: InvoiceMailCandidate = { ...activeCandidate, status: "confirmed" };
      setLocalCandidate(filed);
      onUpdated?.(filed);
      onConfirmed();
      setStep(3);
      toast.success("Rechnung abgelegt. Als Nächstes die Freigabe zuweisen.");
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Freigabe fehlgeschlagen.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleAssign() {
    if (!activeCandidate) return;
    if (activeCandidate.status !== "confirmed") {
      setError("Die Rechnung wird zuerst abgelegt und danach zugewiesen.");
      setStep(2);
      return;
    }
    if (assigneeLogins.length === 0) {
      setError("Bitte mindestens eine Person aus dem Büro wählen.");
      return;
    }
    setAssigning(true);
    setError(null);
    try {
      const detail = await assignInvoiceApproval(activeCandidate.id, assigneeLogins);
      const people = detail.approval.participants || [];
      const login = detail.approval.assignee_login || assigneeLogins[0] || "";
      const name =
        people.map((person) => person.name || person.login).filter(Boolean).join(", ") ||
        detail.approval.assignee_name ||
        login;
      const status = detail.approval.status;
      const next: InvoiceMailCandidate = {
        ...activeCandidate,
        approval: {
          status,
          assignee_login: login,
          assignee_name: name,
          participants: people,
        },
      };
      setLocalCandidate(next);
      setShowAssigneePicker(false);
      onUpdated?.(next);
      onAssigned?.(activeCandidate.id, login, name, status);
      toast.success(
        people.length > 1
          ? `Zur Freigabe an ${name} zugewiesen. Die Rechnung gilt erst als freigegeben, wenn alle zugestimmt haben.`
          : `Zur Freigabe an ${name} zugewiesen.`,
      );
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Zuweisen fehlgeschlagen.";
      setError(msg);
    } finally {
      setAssigning(false);
    }
  }

  async function handleDismiss() {
    if (!activeCandidate) return;
    setDismissing(true);
    setError(null);
    try {
      await dismissInvoiceMailCandidate(activeCandidate.id);
      toast.success("Vorschlag verworfen.");
      onDismissed?.();
      onClose();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Verwerfen fehlgeschlagen.";
      setError(msg);
    } finally {
      setDismissing(false);
      setDiscardAsk(false);
    }
  }

  async function handleReanalyze() {
    if (!activeCandidate) return;
    setReanalyzing(true);
    setError(null);
    try {
      await reanalyzeInvoiceMailCandidate(activeCandidate.id);
      toast.success("Mail erneut analysiert.");
      const fresh = (await onReanalyzed?.()) || null;
      if (fresh) {
        setLocalCandidate(fresh);
        setSelectedTargets(initialSelectedTargets(fresh));
        setAttachmentTks(initAttachmentTks(fresh));
        setAssigneeLogins(initialAssignees(fresh, approvers));
        setShowAssigneePicker(false);
        setStep(startStep(fresh));
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Analyse fehlgeschlagen.";
      setError(msg);
    } finally {
      setReanalyzing(false);
    }
  }

  if (!activeCandidate) return null;

  const previewTks = resolvedPreviewId ? attachmentTks[resolvedPreviewId] || [] : [];
  const selectedAuftragIds = new Set(selectedTargets.map((target) => target.auftrag_id));
  const projectSuggestions = (activeCandidate.match_targets || []).filter(
    (target) =>
      isProjectMatch(target) &&
      target.auftrag_id != null &&
      !selectedAuftragIds.has(target.auftrag_id),
  );
  const recognizedProjects = (activeCandidate.matched_bestellnummern || []).filter((value) =>
    /^\d+(?:-\d+)+#\d+/.test(value),
  );
  const unmatchedProjectNumbers = recognizedProjects.filter(
    (value) =>
      !(activeCandidate.match_targets || []).some((target) => {
        const needle = value.toUpperCase();
        return [target.matched_fragment, target.auftragsnummer].some(
          (entry) => (entry || "").toUpperCase() === needle,
        );
      }),
  );
  const codes = detectedKuerzels(activeCandidate);
  const filed = activeCandidate.status === "confirmed";
  const assigned = Boolean(activeCandidate.approval?.assignee_login);
  const locked = approvalLocked(activeCandidate.approval?.status);
  const showAssigneeForm = filed && !locked && (!assigned || showAssigneePicker);
  const suggestion = activeCandidate.assignment_suggestion;
  const rankedApprovers = [...approvers].sort((a, b) => {
    const aHit = codes.includes(approverKuerzel(a)) ? 0 : 1;
    const bHit = codes.includes(approverKuerzel(b)) ? 0 : 1;
    if (aHit !== bHit) return aHit - bHit;
    return a.name.localeCompare(b.name, "de");
  });
  const filedLinks = [
    ...(activeCandidate.sharepoint_results?.eml || []).map((file) => ({
      key: `eml-${file.path || file.auftragsnummer || ""}`,
      href: file.web_url,
      label: file.path || file.auftragsnummer || "SharePoint",
    })),
    ...(activeCandidate.sharepoint_results?.attachments || []).map((file) => ({
      key: `att-${file.path || file.filename || ""}`,
      href: file.web_url,
      label: file.path || file.filename || "Anhang",
    })),
  ];
  const busy = loading || savingMatches || assigning || reanalyzing || dismissing || merging;
  const stepMeta = WIZARD_STEPS[step - 1];

  function goTo(next: WizardStep) {
    if (next === 3 && !filed) {
      setError("Die Rechnung wird zuerst abgelegt und danach den Personen zugewiesen.");
      setStep(2);
      return;
    }
    if (next > 1 && isPending && selectedAttachments.size === 0) {
      setError("Bitte mindestens eine Datei für die Ablage auswählen. Logos sind voraus abgewählt.");
      setStep(1);
      return;
    }
    setError(null);
    setDiscardAsk(false);
    if (next !== 3) setShowAssigneePicker(false);
    setStep(next);
  }

  const footer = (
    <>
      {step === 1 && isPending && discardAsk ? (
        <p className="mr-auto text-sm text-label-secondary">Diese Mail ohne Ablage verwerfen?</p>
      ) : (
        <p className="mr-auto hidden text-xs text-label-tertiary sm:block">
          {step === 3 && assigned && !showAssigneePicker
            ? "Erledigt · hier ist nichts mehr zu tun"
            : `Schritt ${step} von 3 · ${stepMeta?.hint}`}
        </p>
      )}
      {step === 1 ? (
        discardAsk ? (
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setDiscardAsk(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => void handleDismiss()}>
              {dismissing ? <Spinner className="h-3.5 w-3.5" /> : "Verwerfen"}
            </Button>
          </>
        ) : (
          <>
            {isPending ? (
              <Button variant="ghost" disabled={busy} onClick={() => setDiscardAsk(true)}>
                Verwerfen
              </Button>
            ) : null}
            {isPending ? (
              <Button variant="secondary" disabled={busy} onClick={() => void handleReanalyze()}>
                {reanalyzing ? <Spinner className="h-3.5 w-3.5" /> : "Erneut analysieren"}
              </Button>
            ) : null}
            <Button variant="secondary" disabled={busy} onClick={onClose}>
              Schließen
            </Button>
            {isPending ? (
              <Button disabled={busy} onClick={() => goTo(2)}>
                Weiter zur Ablage
              </Button>
            ) : filed ? (
              <Button disabled={busy} onClick={() => goTo(3)}>
                Weiter zur Freigabe
              </Button>
            ) : null}
          </>
        )
      ) : null}
      {step === 2 ? (
        <>
          <Button variant="secondary" disabled={busy} onClick={() => goTo(1)}>
            Zurück
          </Button>
          {isPending ? (
            <Button
              variant="secondary"
              disabled={busy || selectedTargets.length === 0}
              onClick={() => void handleSaveMatches()}
            >
              {savingMatches ? <Spinner className="h-3.5 w-3.5" /> : "Zuordnung speichern"}
            </Button>
          ) : null}
          {isPending ? (
            <Button disabled={busy || selectedTargets.length === 0} onClick={() => void handleConfirm()}>
              {loading ? <Spinner className="h-3.5 w-3.5" /> : "Ablegen"}
            </Button>
          ) : (
            <Button disabled={!filed} onClick={() => goTo(3)}>
              Weiter zur Freigabe
            </Button>
          )}
        </>
      ) : null}
      {step === 3 ? (
        <>
          <Button variant="secondary" disabled={busy} onClick={() => goTo(2)}>
            Zurück
          </Button>
          <Button variant="secondary" disabled={busy} onClick={onClose}>
            Schließen
          </Button>
          {hasNext && onNext ? (
            <Button variant={activeCandidate.approval?.assignee_login ? "primary" : "secondary"} disabled={busy} onClick={onNext}>
              Nächste Rechnung
            </Button>
          ) : null}
          {showAssigneeForm ? (
            <Button
              variant={assigned && hasNext ? "secondary" : "primary"}
              disabled={busy || assigneeLogins.length === 0}
              onClick={() => void handleAssign()}
            >
              {assigning ? (
                <Spinner className="h-3.5 w-3.5" />
              ) : assigned ? (
                "Neu zuweisen"
              ) : (
                "Zur Freigabe zuweisen"
              )}
            </Button>
          ) : null}
        </>
      ) : null}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="4xl"
      fullScreenMobile
      title={activeCandidate.subject || "Rechnung"}
      description={`${senderLabel(activeCandidate)} · ${activeCandidate.received_at ? fmtDateTime(activeCandidate.received_at) : "ohne Datum"}`}
      panelClassName="sm:h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-2rem)]"
      bodyClassName="!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-0 max-sm:!overflow-hidden"
      footer={footer}
    >
      <div className="flex h-full min-h-0 flex-col lg:flex-row">
        <div className="flex h-[min(38dvh,20rem)] min-h-0 shrink-0 flex-col border-b border-separator bg-fill-quaternary/25 lg:h-full lg:w-[56%] lg:border-b-0 lg:border-r">
          <div className="flex shrink-0 items-center gap-2 border-b border-separator bg-card px-3 py-2">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">Beleg</span>
            {orderedAttachments.length > 1 ? (
              <select
                className="eh-input h-8 min-w-0 flex-1 py-1 text-xs"
                value={previewAtt?.id || ""}
                onChange={(event) => setPreviewId(event.target.value)}
              >
                {orderedAttachments.map((item) => (
                  <option key={item.id || item.name} value={item.id || ""}>
                    {item.name || "Anhang"}
                  </option>
                ))}
              </select>
            ) : (
              <span className="min-w-0 truncate text-xs text-label-secondary">{previewAtt?.name || "Kein Anhang"}</span>
            )}
          </div>
          <div className="relative min-h-0 flex-1">
            {previewAtt ? (
              <InvoiceMailAttachmentPreview
                key={`${activeCandidate.id}:${previewAtt.id}`}
                candidateId={activeCandidate.id}
                attachment={previewAtt}
                className="absolute inset-0 h-full w-full rounded-none border-0 bg-white"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-label-secondary">
                Kein Belegbild vorhanden.
              </div>
            )}
          </div>
          {previewAtt && isPending ? (
            <div className="shrink-0 space-y-2 border-t border-separator bg-card px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                {previewTks.length === 0 ? (
                  <span className="text-xs text-label-secondary">Noch keine Nummer am Beleg</span>
                ) : (
                  previewTks.map((tk) => (
                    <span
                      key={tk}
                      className="inline-flex items-center gap-1 rounded-md bg-accent/10 px-2 py-0.5 font-mono text-xs text-accent"
                    >
                      {tk}
                      <button
                        type="button"
                        className="text-label-secondary hover:text-status-red"
                        onClick={() => removeTkFromPreviewAttachment(tk)}
                        aria-label={`${tk} entfernen`}
                      >
                        ×
                      </button>
                    </span>
                  ))
                )}
              </div>
              <div className="flex gap-2">
                <input
                  className="eh-input min-w-0 flex-1 py-1.5 font-mono text-xs"
                  placeholder="Nummer, z. B. TK26-1-1#123-706-FLB"
                  value={newTkInput}
                  onChange={(event) => setNewTkInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addTkToPreviewAttachment(newTkInput);
                    }
                  }}
                />
                <Button size="sm" variant="secondary" onClick={() => addTkToPreviewAttachment(newTkInput)}>
                  Hinzufügen
                </Button>
              </div>
              <label className="flex items-center gap-2 text-xs text-label-secondary">
                <input
                  type="checkbox"
                  checked={selectedAttachments.has(String(previewAtt.id))}
                  onChange={() => toggleAttachment(String(previewAtt.id))}
                />
                Beim Ablegen mitnehmen
                {fmtAttachmentSize(previewAtt.size ?? undefined) ? (
                  <span className="text-label-tertiary">{fmtAttachmentSize(previewAtt.size ?? undefined)}</span>
                ) : null}
                <a
                  href={invoiceMailAttachmentUrl(activeCandidate.id, String(previewAtt.id))}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent hover:underline"
                >
                  Neuer Tab
                </a>
              </label>
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <ol className="grid shrink-0 grid-cols-3 gap-1.5 border-b border-separator bg-card px-3 py-2.5 sm:px-4" aria-label="Schritte">
            {WIZARD_STEPS.map((item) => {
              const done = item.step === 1 ? step > 1 || filed : item.step === 2 ? filed : Boolean(activeCandidate.approval?.assignee_login);
              const active = item.step === step;
              const reachable = item.step < 3 || filed;
              return (
                <li key={item.step}>
                  <button
                    type="button"
                    disabled={!reachable}
                    onClick={() => goTo(item.step)}
                    className={clsx(
                      "flex w-full items-center gap-2 rounded-xl border px-2 py-2 text-left transition",
                      active && done
                        ? "border-status-green bg-status-green/10"
                        : active
                          ? "border-accent bg-accent/10"
                          : "border-separator bg-card hover:bg-fill-quaternary/40",
                      !reachable && "cursor-not-allowed opacity-50 hover:bg-card",
                    )}
                    aria-current={active ? "step" : undefined}
                  >
                    <span
                      className={clsx(
                        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                        done
                          ? "bg-status-green text-label-on-accent"
                          : active
                            ? "bg-accent text-label-on-accent"
                            : "bg-fill-tertiary text-label-secondary",
                      )}
                    >
                      {done ? <CheckIcon className="h-3.5 w-3.5" aria-hidden /> : item.step}
                    </span>
                    <span className="min-w-0">
                      <span className={clsx("block truncate text-sm font-semibold", active || done ? "text-label" : "text-label-secondary")}>
                        {item.label}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-4 py-4 text-sm sm:px-5">
            {saveOk ? <p className="text-sm text-status-green">{saveOk}</p> : null}
            {error ? <p className="rounded-lg bg-status-red/10 px-3 py-2 text-sm text-status-red">{error}</p> : null}

            {step === 1 ? (
              <section className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(activeCandidate.status)}`}>
                    {statusLabel(activeCandidate.status)}
                  </span>
                  {einvoiceLines > 0 ? (
                    <Link
                      to={`/bestellwesen/eingangsrechnung-artikel?candidate_id=${activeCandidate.id}`}
                      className="rounded-full bg-status-green/15 px-2.5 py-0.5 text-xs font-semibold text-status-green hover:underline"
                    >
                      E-Rechnung · {einvoiceLines} Position{einvoiceLines === 1 ? "" : "en"}
                    </Link>
                  ) : (
                    <span className="rounded-full bg-fill-tertiary px-2.5 py-0.5 text-xs font-semibold text-label-secondary">
                      Keine E-Rechnung
                    </span>
                  )}
                </div>
                {activeCandidate.snippet ? (
                  <p className="rounded-xl border border-separator bg-fill-quaternary/20 px-3 py-2 text-label-secondary">
                    {activeCandidate.snippet}
                  </p>
                ) : null}
                {isPending && attachments.length > 0 ? (
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                      Dateien für die Ablage
                    </div>
                    <ul className="space-y-1.5">
                      {orderedAttachments.map((item, index) => {
                        const id = String(item.id);
                        const included = selectedAttachments.has(id);
                        const hint = fileExclusionHint(item);
                        return (
                          <li key={id} className="flex items-center gap-1.5 rounded-lg border border-separator bg-card px-2 py-2">
                            {orderedAttachments.length > 1 ? (
                              <div className="flex shrink-0 flex-col">
                                <button
                                  type="button"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded text-label-secondary hover:bg-fill-tertiary disabled:opacity-30"
                                  onClick={() => moveFile(id, -1)}
                                  disabled={index === 0 || merging}
                                  aria-label={`${item.name || "Datei"} nach oben`}
                                  title="Nach oben"
                                >
                                  <ChevronUpIcon className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex h-5 w-5 items-center justify-center rounded text-label-secondary hover:bg-fill-tertiary disabled:opacity-30"
                                  onClick={() => moveFile(id, 1)}
                                  disabled={index === orderedAttachments.length - 1 || merging}
                                  aria-label={`${item.name || "Datei"} nach unten`}
                                  title="Nach unten"
                                >
                                  <ChevronDownIcon className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : null}
                            <label className="flex min-w-0 flex-1 items-center gap-2">
                              <input
                                type="checkbox"
                                checked={included}
                                onChange={() => toggleAttachment(id)}
                              />
                              <span className="min-w-0 flex-1">
                                <span className={clsx("block truncate", included ? "text-label" : "text-label-tertiary line-through")}>
                                  {orderedAttachments.length > 1 ? `${index + 1}. ` : ""}
                                  {item.name || "Anhang"}
                                </span>
                                <span className="text-xs text-label-tertiary">
                                  {fmtAttachmentSize(item.size ?? undefined)}
                                  {item.merged ? " · zusammengefügt" : ""}
                                  {hint ? ` · ${hint}` : ""}
                                  {included ? "" : " · wird nicht abgelegt"}
                                </span>
                              </span>
                            </label>
                            <button
                              type="button"
                              className="shrink-0 text-xs text-accent hover:underline"
                              onClick={() => setPreviewId(id)}
                            >
                              Ansehen
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {orderedAttachments.length > 1 ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={merging || Boolean(blockingMerge) || selectedMergeIds.length < 2}
                          onClick={() => void handleMerge()}
                          title={
                            blockingMerge
                              ? "Nur PDF-Dateien und Bilder lassen sich zusammenfügen."
                              : "Fügt die angehakten Dateien in dieser Reihenfolge zu einer PDF zusammen."
                          }
                        >
                          {mergeAction === "merge" ? <Spinner className="h-3.5 w-3.5" /> : "Zu einer Datei zusammenfügen"}
                        </Button>
                        {hasMergedFile ? (
                          <Button size="sm" variant="ghost" disabled={merging} onClick={() => void handleUnmerge()}>
                            {mergeAction === "split" ? <Spinner className="h-3.5 w-3.5" /> : "Zusammenführung aufheben"}
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                    <p className="mt-2 text-xs text-label-secondary">
                      Logos und eingebettete Bilder sind voraus abgewählt. Nur angehakte Dateien landen im Projektordner.
                      {orderedAttachments.length > 1
                        ? " Die Pfeile ändern die Reihenfolge. Zusammengefügt werden die angehakten PDF-Dateien und Bilder in dieser Reihenfolge."
                        : ""}
                    </p>
                  </div>
                ) : null}
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">Erkannte Nummern</div>
                  {(activeCandidate.matched_bestellnummern || []).length === 0 ? (
                    <p className="text-label-secondary">Keine Bestell- oder Projektnummer erkannt. Die Nummer kannst du am Beleg ergänzen.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {(activeCandidate.matched_bestellnummern || []).map((tk) => (
                        <span key={tk} className="rounded-md bg-accent/10 px-2 py-0.5 font-mono text-xs text-accent">
                          {tk}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {codes.length > 0 || suggestion?.reason ? (
                  <div className="rounded-xl border border-separator bg-card px-3 py-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Kürzel für die Freigabe</div>
                    {codes.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {codes.map((code) => (
                          <span key={code} className="rounded-md bg-fill-tertiary px-2 py-0.5 font-mono text-xs font-semibold text-label">
                            {code}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {suggestion?.reason ? <p className="mt-2 text-label-secondary">{suggestion.reason}</p> : null}
                    <p className="mt-2 text-xs text-label-tertiary">Die Zuweisung kommt nach dem Ablegen.</p>
                  </div>
                ) : null}
                {einvoiceLoading ? (
                  <div className="flex items-center gap-2 text-label-secondary">
                    <Spinner className="h-4 w-4" />
                    E-Rechnungspositionen werden geladen…
                  </div>
                ) : null}
                {!einvoiceLoading && (einvoice?.lines?.length || 0) > 0 ? (
                  <div className="overflow-hidden rounded-xl border border-separator">
                    <div className="border-b border-separator px-3 py-2 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                      Positionen
                    </div>
                    <ul className="divide-y divide-separator">
                      {(einvoice?.lines || []).slice(0, 8).map((line: EingangsrechnungArtikelLine) => (
                        <li key={line.id} className="flex items-start justify-between gap-3 px-3 py-2">
                          <div className="min-w-0">
                            <div className="font-medium">{line.bezeichnung}</div>
                            <div className="text-xs text-label-secondary">
                              {line.stamm_artikel_id ? stammMatchLabel(line) : "Kein Stamm-Match"}
                            </div>
                          </div>
                          <div className="shrink-0 text-right text-xs tabular-nums text-label-secondary">
                            <div>{fmtQty(line.menge)}</div>
                            <div>{formatEuro(line.zeilen_netto, 2)}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {(einvoice?.lines.length || 0) > 8 ? (
                      <p className="border-t border-separator px-3 py-2 text-xs text-label-secondary">
                        … und {(einvoice?.lines.length || 0) - 8} weitere.{" "}
                        <Link
                          to={`/bestellwesen/eingangsrechnung-artikel?candidate_id=${activeCandidate.id}`}
                          className="text-accent hover:underline"
                        >
                          Alle anzeigen
                        </Link>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </section>
            ) : null}

            {step === 2 ? (
              <section className="space-y-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Ablageziel</div>
                  <p className="mt-1 text-label-secondary">
                    Die Rechnung landet unter <span className="font-mono text-xs">{defaultSubfolder}/</span>
                  </p>
                </div>
                {filed && filedLinks.length > 0 ? (
                  <ul className="space-y-1 rounded-xl border border-status-green/30 bg-status-green/10 px-3 py-2 text-xs">
                    {filedLinks.map((file) => (
                      <li key={file.key}>
                        {file.href ? (
                          <a href={file.href} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                            {file.label}
                          </a>
                        ) : (
                          <span>{file.label}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {selectedTargets.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-separator px-3 py-4 text-label-secondary">
                    Noch kein Ziel. Wähle ein vorgeschlagenes Projekt oder suche eine Nummer.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {selectedTargets.map((t) => {
                      const meta = (activeCandidate.match_targets || []).find((m) => {
                        if (m.auftrag_id !== t.auftrag_id) return false;
                        if (t.matchKind === "projekt") return isProjectMatch(m);
                        return (m.bestellnummer || "").toUpperCase() === t.bestellnummer.toUpperCase();
                      });
                      return (
                        <li key={targetKey(t)} className="rounded-xl border border-separator bg-card px-3 py-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              {meta?.match_confidence ? (
                                <span className={`mr-2 rounded px-1.5 py-0.5 text-xs font-medium ${confidenceBadgeClass(meta.match_confidence)}`}>
                                  {confidenceLabel(meta.match_confidence)}
                                </span>
                              ) : (
                                <span className="mr-2 rounded bg-accent/10 px-1.5 py-0.5 text-xs font-medium text-accent">Manuell</span>
                              )}
                              <span className="font-mono text-sm text-accent">{t.displayNumber}</span>
                              <div className="mt-1 text-label">{t.label}</div>
                            </div>
                            {isPending ? (
                              <button
                                type="button"
                                className="text-label-secondary hover:text-status-red"
                                onClick={() => removeTarget(t)}
                                aria-label="Ziel entfernen"
                              >
                                ×
                              </button>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {projectSuggestions.length > 0 || unmatchedProjectNumbers.length > 0 ? (
                  <div className="space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wide text-label-tertiary">Mögliche Projekte</div>
                    {unmatchedProjectNumbers.length > 0 ? (
                      <p className="text-xs text-status-orange">
                        Projektnummer {unmatchedProjectNumbers.join(", ")} erkannt, kein passendes Projekt gefunden.
                      </p>
                    ) : null}
                    {projectSuggestions.length > 0 ? (
                      <ul className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-separator p-1.5">
                        {projectSuggestions.map((item) => {
                          const canFile = Boolean(item.sharepoint_folder_id);
                          return (
                            <li key={`${item.auftrag_id}-${item.jahr || ""}`}>
                              <button
                                type="button"
                                disabled={!isPending || !canFile}
                                className="w-full rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent/10 disabled:cursor-default disabled:hover:bg-transparent"
                                onClick={() => addSearchResult(item)}
                              >
                                <span className="font-mono text-accent">{item.auftragsnummer || item.matched_fragment}</span>
                                <span className="ml-2 text-label-secondary">{projectDetails(item) || "Projekt"}</span>
                                {!canFile ? <span className="ml-2 text-status-orange">ohne SharePoint-Ordner</span> : null}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
                {isPending ? (
                  <div className="space-y-2">
                    <input
                      className="eh-input"
                      placeholder="Bestellnummer oder Projektnummer suchen…"
                      value={searchQ}
                      onChange={(event) => setSearchQ(event.target.value)}
                    />
                    {searchLoading ? <div className="text-xs text-label-secondary">Suche…</div> : null}
                    {searchResults.length > 0 ? (
                      <ul className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-separator p-1.5">
                        {searchResults.map((item) => {
                          const project = isProjectMatch(item);
                          return (
                            <li key={`${item.auftrag_id}-${item.bestellnummer || item.auftragsnummer}-${item.jahr || ""}`}>
                              <button
                                type="button"
                                className="w-full rounded-lg px-2 py-1.5 text-left text-xs hover:bg-accent/10"
                                onClick={() => addSearchResult(item)}
                              >
                                <span className="font-mono text-accent">{project ? item.auftragsnummer : item.bestellnummer}</span>
                                <span className="ml-2 text-label-secondary">
                                  {project
                                    ? projectDetails(item) || "Projekt"
                                    : `Auftrag ${item.auftragsnummer || item.auftrag_id}${item.auftrag_beschreibung ? ` – ${item.auftrag_beschreibung}` : ""}`}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
                {isPending ? (
                  <div className="space-y-2 rounded-xl border border-separator bg-fill-quaternary/20 px-3 py-3">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={includeEml} onChange={(event) => setIncludeEml(event.target.checked)} />
                      Mail als .eml mit ablegen
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={moveInbox} onChange={(event) => setMoveInbox(event.target.checked)} />
                      Mail danach in „Verarbeitet“ verschieben
                    </label>
                  </div>
                ) : null}
              </section>
            ) : null}

            {step === 3 ? (
              <section className="space-y-3">
                {!filed ? (
                  <p className="rounded-xl border border-status-orange/30 bg-status-orange/10 px-3 py-3 text-status-orange">
                    Diese Rechnung ist noch nicht abgelegt. Die Freigabe folgt danach.
                  </p>
                ) : assigned && !showAssigneePicker ? (
                  <AssignmentDonePanel
                    candidate={activeCandidate}
                    targets={selectedTargets}
                    links={filedLinks}
                    codes={codes}
                    approvers={approvers}
                    einvoiceLines={einvoiceLines}
                    locked={locked}
                    onEdit={() => setShowAssigneePicker(true)}
                  />
                ) : (
                  <>
                    {showAssigneePicker ? (
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-label-secondary">
                          Wähle die Personen neu. Die Rechnung gilt erst als freigegeben, wenn alle zugestimmt haben.
                        </p>
                        <button
                          type="button"
                          className="shrink-0 text-xs text-accent hover:underline"
                          onClick={() => setShowAssigneePicker(false)}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : (
                      <p className="text-label-secondary">
                        Die Rechnung liegt im Projekt. Wähle eine oder mehrere Personen. Sie gilt erst als freigegeben, wenn alle ausgewählten Personen zugestimmt haben. Das Kürzel ist der Windows-Login und steht so an der TK-Nummer.
                      </p>
                    )}
                    {suggestion?.reason ? (
                      <div className={clsx("rounded-xl border px-3 py-3", suggestion.login ? "border-status-green/30 bg-status-green/10" : "border-status-orange/30 bg-status-orange/10")}>
                        {codes.length > 0 ? (
                          <div className="mb-2 flex flex-wrap gap-1.5">
                            {codes.map((code) => (
                              <span key={code} className="rounded-md bg-card px-2 py-0.5 font-mono text-xs font-semibold text-label">
                                {code}
                              </span>
                            ))}
                          </div>
                        ) : null}
                        {suggestion.login && suggestion.name ? (
                          <p className="font-medium text-label">Vorschlag: {suggestion.name}</p>
                        ) : null}
                        <p className="text-label-secondary">{suggestion.reason}</p>
                      </div>
                    ) : null}
                    {assigneeLogins.length > 0 ? (
                      <p className="text-xs text-label-secondary">
                        {assigneeLogins.length === 1
                          ? "1 Person ausgewählt."
                          : `${assigneeLogins.length} Personen ausgewählt. Alle müssen freigeben.`}
                      </p>
                    ) : (
                      <p className="text-xs text-status-orange">Noch keine Person ausgewählt.</p>
                    )}
                    <ul className="space-y-1.5">
                      {rankedApprovers.map((person) => {
                        const code = approverKuerzel(person);
                        const hit = Boolean(code) && codes.includes(code);
                        const selected = assigneeLogins.includes(person.login);
                        return (
                          <li key={person.login}>
                            <button
                              type="button"
                              disabled={!filed}
                              onClick={() =>
                                setAssigneeLogins((prev) =>
                                  prev.includes(person.login)
                                    ? prev.filter((login) => login !== person.login)
                                    : [...prev, person.login],
                                )
                              }
                              className={clsx(
                                "flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition",
                                selected ? "border-accent bg-accent/10" : "border-separator bg-card hover:border-accent/40",
                                !filed && "cursor-not-allowed opacity-60",
                              )}
                            >
                              <span className="min-w-0">
                                <span className="block truncate font-medium text-label">{person.name}</span>
                                {hit ? <span className="text-xs text-status-green">Kürzel auf dieser Rechnung</span> : null}
                              </span>
                              {code ? (
                                <span className="shrink-0 rounded-md bg-fill-tertiary px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-label">
                                  {code}
                                </span>
                              ) : null}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </section>
            ) : null}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
